import { useAtomValue } from "jotai"
import { useContext, useMemo } from "react"
import { AuthContext } from "./AuthContext"
import { AuthService } from "./AuthService"
import { authStateAtom } from "./atoms"

export const useAuth = () => {
  const authState = useAtomValue(authStateAtom)

  // Get auth service from context
  const authService = useAuthService()

  // Memoize bound methods to prevent recreation on every render
  const boundMethods = useMemo(
    () => ({
      register: authService.register.bind(
        authService,
      ) as typeof authService.register,
      login: authService.login.bind(authService) as typeof authService.login,
      verifyTwoFactor: authService.verifyTwoFactor.bind(
        authService,
      ) as typeof authService.verifyTwoFactor,
      logout: authService.logout.bind(authService) as typeof authService.logout,
      clearAuthData: authService.clearAuthData.bind(
        authService,
      ) as typeof authService.clearAuthData,
      getAccessToken: authService.getAccessToken.bind(
        authService,
      ) as typeof authService.getAccessToken,
      clearError: authService.clearError.bind(
        authService,
      ) as typeof authService.clearError,
      refreshAccessToken: authService.refreshTokenSilently.bind(
        authService,
      ) as typeof authService.refreshTokenSilently,
      requestMagicLink: authService.requestMagicLink.bind(
        authService,
      ) as typeof authService.requestMagicLink,
      verifyMagicLink: authService.verifyMagicLink.bind(
        authService,
      ) as typeof authService.verifyMagicLink,
    }),
    [authService],
  )

  return {
    ...authState,
    ...boundMethods,
  }
}

export const useAuthService = (): AuthService => {
  // This would come from React Context
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuthService must be used within AuthProvider")
  }
  return context.authService
}
